// Booth Tracker v2 data model (brief §"Data model — booth record").
//
// Key decisions baked in here:
//  - `id` is a uuid, NOT `booth-{number}` — February's booth-numbered ids collided.
//  - Uniqueness for de-dup/merge is `show + booth_number` (the `show_booth` index),
//    not the id. booth_number may repeat across shows, so it's never unique alone.
//  - Photo *bytes* live in a separate `photos` store (StoredPhoto) so booth records
//    stay light; the booth record holds only photo references (PhotoRef).
//  - Sync fields are present from Slice 1 but always start local/unsynced; the
//    sync engine (Slice 2) is the only thing that flips them.

import type { Show, Priority, PhotoKind } from '../config';

export interface PhotoRef {
  local_id: string;
  kind: PhotoKind;
  /** Server URL once uploaded (Slice 2). null while local-only. */
  blob_url: string | null;
  synced: boolean;
  /**
   * v1.1: free-text per-photo note (price, pack, colours — whatever the buyer
   * wants against THIS picture). Deliberately on the PhotoRef, not StoredPhoto:
   * refs ride inside the booth record, which the backend round-trips as one JSON
   * blob (`photos` column), so a note reaches other devices with no API change.
   * The bytes store stays local-only. Absent/empty = no note.
   */
  note?: string;
}

/**
 * Actual compressed image bytes, stored separately and keyed by local_id.
 *
 * Bytes are held as an `ArrayBuffer` (+ mime `type`), NOT a `Blob`: iOS/WebKit
 * IndexedDB is unreliable at round-tripping Blob values (a `put` of a Blob can
 * fail outright — the "Save failed" symptom on iPad), whereas ArrayBuffers are
 * a structured-clone primitive it handles reliably. Reconstruct a Blob on read
 * with `photoBlob()` everywhere the image is displayed or uploaded.
 */
export interface StoredPhoto {
  local_id: string;
  booth_id: string;
  kind: PhotoKind;
  buffer: ArrayBuffer;
  type: string; // mime, e.g. image/jpeg — used to rebuild the Blob
  width: number;
  height: number;
  bytes: number;
  synced: boolean;
  created_at: string;
}

/** Rebuild a Blob from a StoredPhoto's bytes (for thumbnails + upload). */
export function photoBlob(p: StoredPhoto): Blob {
  return new Blob([p.buffer], { type: p.type || 'image/jpeg' });
}

export interface MatchedVendor {
  vendor_id: string;
  name: string;
  confidence: number;
}

export type SyncStatus = 'local' | 'synced';

export interface BoothRecord {
  id: string; // uuid
  show: Show;
  booth_number: string; // raw, may repeat across shows
  vendor_name: string;
  captured_by: string; // from Entra identity (stub in Slice 1)
  group: string; // team/group label
  captured_at: string; // ISO
  updated_at: string; // ISO
  photos: PhotoRef[];
  priority: Priority;
  summary: string; // one-line product description
  samples_taken: boolean;
  vendor_contact: string;
  category_tags: string[];
  matched_vendor: MatchedVendor | null; // from snapshot fuzzy match (Slice 4)
  notes: string;
  sync_status: SyncStatus;

  // v0.5 additions ----------------------------------------------------------
  /**
   * Soft-delete tombstone. Absent/false = live. A delete sets this true, bumps
   * `updated_at`, and syncs — it flows through the normal last-write-wins path so
   * the tombstone propagates to other devices (a naive hard delete would be
   * resurrected on the next pull). Every read path hides `deleted` records but
   * still STORES them, so the tombstone survives to propagate.
   */
  deleted?: boolean;
  /** Free-text detail fields, revealed under "More details" (snake_case to match). */
  ships_from?: string;
  case_packs?: string;
  size_run?: string;
  total_spent?: string;
}

/** Composite key used by the `show_booth` index for de-dup/merge. */
export const showBoothKey = (show: Show, boothNumber: string): string =>
  `${show}::${boothNumber.trim().toLowerCase()}`;

// v0.7 additions ------------------------------------------------------------

/**
 * A starred exhibitor on the SHARED team hit list (v0.7). Syncs through the same
 * last-write-wins path as booths, so a star Brandon adds shows up on Jordan's phone
 * and an un-star removes it everywhere.
 *
 * Merge identity is `exhibitor_key` (the store's keyPath, and the backend RowKey) —
 * NOT `id`: two devices starring the same exhibitor mint different uuids, and keying
 * on those would give the same company two rows that could never converge. `id` is
 * kept for provenance (which device first starred it).
 *
 * `name_key` is a second identity carried alongside so a star survives the pre-trip
 * data re-pull even if the organizer's booth ids shift — see exhibitorKey() in
 * src/exhibitors/data.ts for why ASD makes that a real risk.
 *
 * Un-starring is a TOMBSTONE (`deleted: true`), never a row delete, for exactly the
 * reason booth deletes are: a hard delete would be resurrected by the next pull.
 */
export interface HitListTarget {
  /** Stable merge key: `id:{showKey}:{boothId}` or `name:{showKey}:{normalized}`. */
  exhibitor_key: string;
  /** Secondary identity — matched as a fallback so a re-pull can't orphan a star. */
  name_key: string;
  id: string; // uuid, provenance only
  show: Show;
  /** Directory slug (#/show/{key}) — scopes the per-show hit-list view. */
  show_key: string;
  name: string;
  booths: string[];
  added_by: string;
  added_at: string;
  updated_at: string;
  deleted?: boolean;
  sync_status: SyncStatus;
}
